import React, { useCallback, useEffect, useRef, useState } from "react"
import { StyleSheet, View, useWindowDimensions } from "react-native"
import Carousel, {
  type ICarouselInstance,
} from "react-native-reanimated-carousel"
import CompDetailImagesList from "./CompDetailImages"
import { type AnalysisCompType, type ArvCompType } from "@/generated/graphql"
import { type AnalysisWithCompsType } from "@/queries/analysis"
import useCompsData from "@/hooks/useCompsData"
import { useComp } from "@/queries/analysis"
import { $selectedCompDetailId } from "@/stores/compsDataStore"
import { LinearGradient } from "expo-linear-gradient"
import * as AnalyzerEvents from "@/analytics/events/analyzer"

interface CompCarouselProps {
  analysis: AnalysisWithCompsType
  selectedCompDetailId: string
  onDragStateChange: (isDragging: boolean) => void
}

const CompCarousel: React.FC<CompCarouselProps> = ({
  analysis,
  selectedCompDetailId,
  onDragStateChange,
}: CompCarouselProps) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const [carouselWidth, setCarouselWidth] = useState(windowWidth)
  const [carouselHeight, setCarouselHeight] = useState(windowHeight)

  const carouselIndex = useRef<number>(0)
  const carouselRef = useRef<ICarouselInstance | null>(null)
  const { data: compDetail } = useComp(analysis.id, selectedCompDetailId)
  const enableDragChange = useRef(false)

  const { sortedCompsWithDetails, addCompDetail } = useCompsData(
    analysis?.property
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      enableDragChange.current = true
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    if (carouselRef.current) {
      const index = sortedCompsWithDetails.findIndex(
        (comp) => comp.arvcomp?.id === selectedCompDetailId
      )
      if (index !== -1 && index !== carouselIndex.current) {
        carouselIndex.current = index
        carouselRef.current.scrollTo({ index, animated: false })
      }
    }
  }, [selectedCompDetailId])

  useEffect(() => {
    if (compDetail?.analysisComp?.arvcomp) {
      addCompDetail(
        compDetail?.analysisComp?.arvcomp.id,
        compDetail?.analysisComp?.arvcomp as ArvCompType
      )
    }
  }, [compDetail])

  const updateSelectedComp = (index: number) => {
    AnalyzerEvents.compDetailsListScrolled({
      scrolledTo: index + 1,
    })
    const comp = sortedCompsWithDetails[index]
    if (carouselIndex.current !== index) {
      carouselIndex.current = index
      $selectedCompDetailId.set(comp.arvcomp?.id!)
    }
  }

  const renderItem = useCallback(({ item }: { item: AnalysisCompType }) => {
    return (
      <CompDetailImagesList
        comp={item}
        onDragStateChange={(drag) => {
          onDragStateChange(drag)
        }}
        onImagePress={(cardIndex) => {
          // TODO: Implement photo zoom view
          console.log("Comp pressed", item.id, item.arvcomp?.id, cardIndex)
          AnalyzerEvents.compCarouselCardClicked({
            cardIndex,
            analysis,
            comp: {
              id: item.arvcomp?.id!,
              address: item.arvcomp?.fullAddress || "",
            },
          })
        }}
      />
    )
  }, [])

  return (
    <View
      className={"flex-1"}
      onLayout={(event) => {
        setCarouselWidth(event.nativeEvent.layout.width)
        setCarouselHeight(event.nativeEvent.layout.height)
      }}
    >
      <Carousel
        ref={carouselRef}
        width={carouselWidth || 400}
        height={carouselHeight / 0.95}
        style={{
          width: carouselWidth,
          height: carouselHeight / 0.95,
        }}
        data={sortedCompsWithDetails}
        renderItem={renderItem}
        mode="parallax"
        modeConfig={{
          parallaxScrollingScale: 0.92,
          parallaxScrollingOffset: 42,
        }}
        onScrollStart={() => {
          if (enableDragChange.current) {
            onDragStateChange(true)
          }
        }}
        onScrollEnd={() => {
          onDragStateChange(false)
        }}
        scrollAnimationDuration={500}
        pagingEnabled={false}
        onSnapToItem={(index) => {
          onDragStateChange(false)
          updateSelectedComp(index)
        }}
        onConfigurePanGesture={(panGesture) => {
          panGesture.activeOffsetX([-10, 10])
        }}
      />

      {/* Left Gradient */}
      <LinearGradient
        colors={["#FFFFFF", "rgba(255, 255, 255, 0)"]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={[styles.gradient, styles.leftGradient]}
      />
      {/* Right Gradient: Transparent to White */}
      <LinearGradient
        colors={["rgba(255, 255, 255, 0)", "#FFFFFF"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.5, y: 0 }}
        style={[styles.gradient, styles.rightGradient]}
      />
    </View>
  )
}

// Styles for the gradients
const styles = StyleSheet.create({
  carouselContainer: {
    position: "relative",
  },
  gradient: {
    position: "absolute",
    height: "100%",
    width: 16, // Adjust the width of the gradient effect here
  },
  leftGradient: {
    left: 0,
  },
  rightGradient: {
    right: 0,
  },
})

export default CompCarousel
